// src/components/CampiIndirizzo.js
// Indirizzo a pezzi (W438): via, numero civico, CAP, comune, provincia. Il comune si sceglie
// dall'elenco ISTAT mentre si scrive (provincia e, se è uno solo, CAP si compilano da soli);
// se CAP, comune e provincia non tornano compare un avviso, senza bloccare. «📍 Controlla
// sulla mappa» apre l'indirizzo completo prima di salvarlo. Regole in utils/indirizzi.js.
import React, { useEffect, useId, useMemo, useState } from "react";
import { leggiComuni, trovaComune, suggerisciComuni, avvisiIndirizzo, capInBreve, linkVerificaMappa, componiIndirizzo } from "../utils/indirizzi";

// l'elenco dei comuni (≈180 KB) si carica solo la prima volta che serve
let cacheComuni = null;
export function useComuni() {
  const [comuni, setComuni] = useState(cacheComuni || []);
  useEffect(() => {
    if (cacheComuni) return undefined;
    let vivo = true;
    import("../utils/comuniItalianiDati").then(m => { cacheComuni = leggiComuni(m.default); if (vivo) setComuni(cacheComuni); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  return comuni;
}

const lblBase = { fontSize: 10.5, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 2 };
const inpBase = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box", background: "#fff" };

/**
 * valore: { via, civico, cap, comune, provincia } · onChange(nuovoValore)
 * errori: { campo: "messaggio" } da mostrare (dopo un tentativo di salvataggio)
 */
export default function CampiIndirizzo({ valore = {}, onChange, errori = {}, titolo = "", obbligatorio = true, inp = inpBase, lbl = lblBase }) {
  const comuni = useComuni();
  const id = useId();
  const v = { via: "", civico: "", cap: "", comune: "", provincia: "", ...valore };
  const cambia = (k, x) => onChange({ ...v, [k]: x });
  const suggeriti = useMemo(() => suggerisciComuni(comuni, v.comune), [comuni, v.comune]);
  const scegliComune = (testo) => {
    const m = String(testo).match(/^(.*?)\s*\(([A-Z]{2})\)\s*$/);
    const nome = m ? m[1] : testo;
    const c = trovaComune(comuni, nome, m ? m[2] : v.provincia);
    if (!c || (!m && c.nome.toLowerCase() !== String(nome).trim().toLowerCase())) { onChange({ ...v, comune: testo }); return; }
    onChange({ ...v, comune: c.nome, provincia: c.sigla, cap: c.cap.length === 1 ? c.cap[0] : v.cap });
  };
  const avvisi = avvisiIndirizzo(comuni, v);
  const comune = trovaComune(comuni, v.comune, v.provincia);
  const piuCap = comune && comune.cap.length > 1 && !comune.cap.includes(String(v.cap).trim());
  const mappa = linkVerificaMappa(v);
  const stella = obbligatorio ? " *" : "";
  const bordo = (k) => (errori[k] ? { ...inp, borderColor: "#f87171", background: "#fef2f2" } : inp);
  const errore = (k) => errori[k] ? <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 2 }}>{errori[k]}</div> : null;
  return (
    <div role="group" aria-label={titolo || "Indirizzo"}>
      {titolo && <div style={{ fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{titolo}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 6 }}>
        <div style={{ gridColumn: "span 5" }}>
          <label style={lbl} htmlFor={`${id}-via`}>Via, piazza, strada{stella}</label>
          <input id={`${id}-via`} style={bordo("via")} value={v.via} onChange={e => cambia("via", e.target.value)} placeholder="es. Via Roma" autoComplete="off"/>
          {errore("via")}
        </div>
        <div style={{ gridColumn: "span 1" }}>
          <label style={lbl} htmlFor={`${id}-civ`}>N. civico{stella}</label>
          <input id={`${id}-civ`} style={bordo("civico")} value={v.civico} onChange={e => cambia("civico", e.target.value)} placeholder="es. 12" autoComplete="off"/>
          {errore("civico")}
        </div>
        <div style={{ gridColumn: "span 1" }}>
          <label style={lbl} htmlFor={`${id}-cap`}>CAP{stella}</label>
          <input id={`${id}-cap`} style={bordo("cap")} value={v.cap} inputMode="numeric" maxLength={5} onChange={e => cambia("cap", e.target.value.replace(/\D/g, ""))} placeholder="5 cifre" autoComplete="off"/>
          {errore("cap")}
        </div>
        <div style={{ gridColumn: "span 4" }}>
          <label style={lbl} htmlFor={`${id}-com`}>Comune{stella}</label>
          <input id={`${id}-com`} style={bordo("comune")} value={v.comune} list={`${id}-elenco`} onChange={e => scegliComune(e.target.value)} placeholder="Scrivi e scegli dall'elenco" autoComplete="off"/>
          <datalist id={`${id}-elenco`}>{suggeriti.map(c => <option key={`${c.nome}-${c.sigla}`} value={`${c.nome} (${c.sigla})`}>{`CAP ${capInBreve(c.cap)}`}</option>)}</datalist>
          {errore("comune")}
        </div>
        <div style={{ gridColumn: "span 1" }}>
          <label style={lbl} htmlFor={`${id}-pr`}>Prov.{stella}</label>
          <input id={`${id}-pr`} style={bordo("provincia")} value={v.provincia} maxLength={2} onChange={e => cambia("provincia", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))} placeholder="es. NA" autoComplete="off"/>
          {errore("provincia")}
        </div>
      </div>
      {piuCap && !avvisi.length && <div style={{ fontSize: 11, color: "#6d28d9", marginTop: 4 }}>💜 {comune.nome} ha più CAP ({capInBreve(comune.cap)}): scrivi quello della via.</div>}
      {avvisi.map((a, i) => <div key={i} role="status" style={{ fontSize: 11.5, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "4px 8px", marginTop: 4 }}>⚠️ {a}</div>)}
      {mappa && componiIndirizzo(v).includes(",") && (
        <div style={{ fontSize: 11.5, marginTop: 4, color: "#6b7280" }}>
          {componiIndirizzo(v)} · <a href={mappa} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontWeight: 600 }}>📍 Controlla sulla mappa</a>
        </div>
      )}
    </div>
  );
}
